import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowUpDown } from 'lucide-react';
import StoreLayout, { PlaceholderImage, WhatsAppFloat, PriceDisplay, resolveImageSrc } from '../../components/store/StoreLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get('cat') || '';
  const [sort, setSort] = useState('name-asc');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cat) params.set('category', cat);
    axios.get(`${BACKEND_URL}/api/store/products?${params.toString()}`)
      .then(r => setProducts(r.data || []))
      .finally(() => setLoading(false));
  }, [cat]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/store/categories`)
      .then(r => setCategories(r.data || []))
      .catch(() => setCategories([]));
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = products;
    if (term) {
      list = list.filter(p =>
        (p.product_name || '').toLowerCase().includes(term) ||
        (p.product_brand || '').toLowerCase().includes(term) ||
        (p.product_size || '').toLowerCase().includes(term) ||
        (p.product_color || '').toLowerCase().includes(term)
      );
    }
    const arr = [...list];
    if (sort === 'price-asc') arr.sort((a, b) => (a.selling_price || 0) - (b.selling_price || 0));
    else if (sort === 'price-desc') arr.sort((a, b) => (b.selling_price || 0) - (a.selling_price || 0));
    else arr.sort((a, b) => (a.product_name || '').localeCompare(b.product_name || ''));
    return arr;
  }, [products, q, sort]);

  const setCat = (b) => {
    if (b) setSearchParams({ cat: b });
    else setSearchParams({});
  };

  return (
    <StoreLayout>
      <WhatsAppFloat />
      <section className="max-w-7xl mx-auto px-4 pt-10 pb-4">
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: '"Playfair Display", serif' }}>Shop</h1>
        <p className="text-sm text-stone-500 mt-1">Browse our full range of interior hardware and finishing products.</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-16">
        {/* Toolbar */}
        <div className="sticky top-16 z-20 bg-white/95 backdrop-blur py-3 border-b border-stone-200 mb-6">
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                data-testid="shop-search"
                placeholder="Search products, brand, size…"
                value={q}
                onChange={e => setQ(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-full border border-stone-300 focus:outline-none focus:border-amber-600 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-stone-400" />
              <select
                data-testid="shop-sort"
                value={sort}
                onChange={e => setSort(e.target.value)}
                className="border border-stone-300 rounded-full px-3 py-2 text-sm"
              >
                <option value="name-asc">Name (A–Z)</option>
                <option value="price-asc">Price (low → high)</option>
                <option value="price-desc">Price (high → low)</option>
              </select>
            </div>
          </div>

          {/* Category chips */}
          {categories.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => setCat('')}
                data-testid="cat-all"
                className={`px-3 py-1 rounded-full text-xs transition ${!cat ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
              >All</button>
              {categories.map(c => (
                <button
                  key={c.brand}
                  onClick={() => setCat(c.brand)}
                  data-testid={`cat-${c.brand}`}
                  className={`px-3 py-1 rounded-full text-xs transition ${cat === c.brand ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                >{c.brand} <span className="text-[10px] opacity-70 ml-1">({c.count})</span></button>
              ))}
            </div>
          )}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-stone-100 rounded-xl" />
                <div className="h-3 bg-stone-100 rounded mt-3 w-3/4" />
                <div className="h-3 bg-stone-100 rounded mt-2 w-1/2" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-stone-500 text-sm border border-dashed border-stone-200 rounded-lg">
            No products match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="shop-grid">
            {filtered.map(p => <ShopCard key={p.product_id} p={p} />)}
          </div>
        )}
      </section>
    </StoreLayout>
  );
}

function ShopCard({ p }) {
  return (
    <Link to={`/product/${p.product_id}`} className="group block" data-testid={`shop-card-${p.product_id}`}>
      <div className="aspect-square rounded-xl overflow-hidden bg-stone-50 border border-stone-200 group-hover:border-amber-500 transition">
        {p.image_urls && p.image_urls[0]
          ? <img src={resolveImageSrc(p.image_urls[0])} alt={p.product_name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
          : <PlaceholderImage className="w-full h-full" />}
      </div>
      <div className="mt-3">
        <div className="text-[11px] text-stone-500 uppercase tracking-widest">{p.product_brand}</div>
        <div className="text-sm font-medium text-stone-800 line-clamp-2 min-h-[2.5rem]">{p.product_name}</div>
        <div className="text-[10px] text-stone-500">{[p.product_size, p.product_color].filter(Boolean).join(' · ')}</div>
        <div className="mt-1">
          <PriceDisplay mrp={p.mrp} selling={p.selling_price} unit={p.unit} size="sm" />
        </div>
      </div>
    </Link>
  );
}
